import React, { useState, useCallback } from "react"

import { SpinnerSimple } from "@izettle/react"
import { useLocalization } from "@izettle/tenko-react"

import { Flex, SlideWrapper } from "@/core/components/shared"
import { useSlider } from "@/core/components/slider"
import { CustomerEmailRegex } from "@/core/utils/constant"
import { useSendReceiptPost } from "@/modules/sign-up/api/use-send-receipt"
import ErrorDialog from "@/modules/sign-up/components/error-dialog"
import { InputSlide } from "@/modules/sign-up/components/input-slide"
import { PhoneSlide } from "@/modules/sign-up/components/phone-slide"
import { SlideIndexs } from "@/modules/sign-up/components/types"
import {
  TrackingAction,
  useSignUpTracking,
} from "@/modules/sign-up/hooks/tracking-sign-up"
import formatPhone from "@/modules/sign-up/shared/phone-format"

export type RetrySendReceiptProps = {
  customerUuid: string
  purchaseUuid: string
}

export const RetrySendReceipt = ({
  customerUuid,
  purchaseUuid,
}: RetrySendReceiptProps) => {
  const { text } = useLocalization()
  const { goTo, params, index } = useSlider()
  const [dialogMessage, setDialogMessage] = useState("")
  const { track } = useSignUpTracking(purchaseUuid)

  const closeDialog = () => {
    setDialogMessage("")
  }

  const { mutate, isLoading } = useSendReceiptPost({
    onSuccess: ({ failureMessage, phone, email }) => {
      if (!failureMessage) {
        if (phone) {
          goTo(SlideIndexs.ContactSent, {
            phone: formatPhone(phone),
          })
        } else {
          goTo(SlideIndexs.ContactSent, { email })
        }
      } else {
        setDialogMessage(`SIGNUP_ERROR_${failureMessage}`)
      }
    },
  })

  const handleRetrySendReceipt = useCallback(
    (name: string, value: string) => {
      if (customerUuid && purchaseUuid) {
        mutate({
          customerUuid,
          purchaseUuid,
          contactInfo: {
            [name]: value,
          },
        })
        const isEmail = name === "email"
        const hasContactChanged = isEmail
          ? params.email !== value
          : params.phone !== value
        track(TrackingAction.ClickedConfirmResendReceipt, {
          receiptSentTo: isEmail ? "EmailAddress" : "PhoneNumber",
          [`${name}Changed`]: hasContactChanged,
        })
      }
    },
    [customerUuid, purchaseUuid, params.email, params.phone],
  )

  if (isLoading) {
    return (
      <Flex vCentered hCentered>
        <SpinnerSimple />
      </Flex>
    )
  }

  return (
    <SlideWrapper className={SlideIndexs.RetrySendReceipt !== index ? "hidden" : ""}>
      {params.email ? (
        <InputSlide
          purchaseUuid={purchaseUuid}
          placeholder={text("SIGNUP_RE_ENTER_YOUR_EMAIL")}
          name="email"
          type="email"
          onConfirm={handleRetrySendReceipt}
          fieldMatch={CustomerEmailRegex}
          fieldMatchMessage={text("SIGNUP_VALID_EMAIL_REQUIRED")}
          nextSlide={SlideIndexs.ContactSent}
          confirmText={text("SIGNUP_SEND_RECEIPT")}
          value={params.email}
        />
      ) : (
        <PhoneSlide
          purchaseUuid={purchaseUuid}
          placeholder={text("SIGNUP_RE_ENTER_YOUR_PHONE")}
          onConfirm={handleRetrySendReceipt}
          confirmText={text("SIGNUP_SEND_RECEIPT")}
          nextSlide={SlideIndexs.ContactSent}
          value={params.phone}
        />
      )}
      <ErrorDialog onClose={closeDialog} message={dialogMessage} />
    </SlideWrapper>
  )
}
